/**
 * Real Evolu data source for Lightning transaction history.
 *
 * Implements LightningHistoryDataSource from useLightningHistory,
 * reading from the Evolu transaction table where type='lightning'.
 * Converts Evolu branded types to plain types for the UI layer.
 * Resolves wallet connection labels for display.
 *
 * Works offline because Evolu stores data in local SQLite (LN-20).
 *
 * Tier: 2 (Lightning features).
 *
 * @module lightning/hooks/useLightningHistoryDataSource
 */

import { useMemo, useRef } from 'react';
import { useEvolu, useQuery } from '@evolu/react';
import type { LightningHistoryDataSource, LightningTransaction } from './useLightningHistory';

/** Options for useLightningHistoryDataSource. */
export interface UseLightningHistoryDataSourceOptions {
  /** Filter transactions to a specific wallet connection. Null = show all. */
  readonly connectionId?: string | null;
}

/** Return type for useLightningHistoryDataSource. */
export interface UseLightningHistoryDataSourceResult {
  /** The real Evolu-backed data source for Lightning history. */
  readonly dataSource: LightningHistoryDataSource;
}

/**
 * Provide a real Evolu data source for Lightning transaction history.
 *
 * Queries the transaction table for type='lightning', non-deleted rows,
 * sorted by settledAt descending (newest first). Parses Evolu branded
 * types into plain LightningTransaction objects. Resolves wallet
 * connection labels from the walletConnection table. Optionally filters
 * to a specific wallet connection.
 *
 * @param options - Optional connectionId filter
 * @returns Object with dataSource for useLightningHistory
 */
export function useLightningHistoryDataSource(
  options?: UseLightningHistoryDataSourceOptions,
): UseLightningHistoryDataSourceResult {
  const evolu = useEvolu();

  const historyQueryRef = useRef(
    evolu.createQuery((db) =>
      db
        .selectFrom('transaction')
        .select([
          'id',
          'direction',
          'amountSats',
          'feeSats',
          'paymentHash',
          'status',
          'note',
          'lightningAddress',
          'settledAt',
          'initiatedAt',
          'type',
          'walletConnectionId',
          'contactId',
        ])
        .where('isDeleted', 'is not', 1 as never)
        .orderBy('settledAt', 'desc'),
    ),
  );
  const historyQuery = historyQueryRef.current;

  const connectionsQueryRef = useRef(
    evolu.createQuery((db) =>
      db
        .selectFrom('walletConnection')
        .select(['id', 'label'])
        .where('isDeleted', 'is not', 1 as never),
    ),
  );
  const connectionsQuery = connectionsQueryRef.current;

  const rows = useQuery(historyQuery);
  const connectionRows = useQuery(connectionsQuery);

  const filterConnectionId = options?.connectionId ?? null;

  // Map stringified transaction IDs back to original Evolu branded IDs
  // for use by tagTransaction (Evolu update needs branded IDs)
  const txRowIdMap = useMemo(() => {
    const map = new Map<string, unknown>();
    for (const row of rows) {
      map.set(String(row.id), row.id);
    }
    return map;
  }, [rows]);

  // Build a lookup map from connection ID to label.
  // Always populate so the Activity page wallet filter shows NWC wallets.
  const connectionLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const conn of connectionRows) {
      if (conn.label != null) {
        map.set(String(conn.id), String(conn.label));
      }
    }
    return map;
  }, [connectionRows]);

  const transactions: readonly LightningTransaction[] = useMemo(
    () =>
      rows
        .filter((row) => {
          if (String(row.type) !== 'lightning') return false;
          // When filtering by connection, only show transactions tagged
          // with that connection ID (untagged legacy transactions excluded)
          if (filterConnectionId) {
            const txConnectionId =
              row.walletConnectionId != null ? String(row.walletConnectionId) : null;
            return txConnectionId === filterConnectionId;
          }
          return true;
        })
        .map((row) => {
          const direction = String(row.direction);
          const status = String(row.status);
          const connId = row.walletConnectionId != null ? String(row.walletConnectionId) : null;

          return {
            id: String(row.id),
            direction: direction === 'send' ? ('send' as const) : ('receive' as const),
            amountSats: row.amountSats as number,
            feeSats: row.feeSats != null ? (row.feeSats as number) : null,
            paymentHash: row.paymentHash != null ? String(row.paymentHash) : null,
            status: status === 'failed' ? ('failed' as const) : ('settled' as const),
            note: row.note != null ? String(row.note) : null,
            lightningAddress: row.lightningAddress != null ? String(row.lightningAddress) : null,
            settledAt: row.settledAt != null ? String(row.settledAt) : null,
            initiatedAt: String(row.initiatedAt),
            walletConnectionId: connId,
            walletLabel: connId ? (connectionLabelMap.get(connId) ?? null) : null,
            contactId: row.contactId != null ? String(row.contactId) : null,
            contactName: null,
          };
        }),
    [rows, filterConnectionId, connectionLabelMap],
  );

  const dataSource: LightningHistoryDataSource = useMemo(
    () => ({ transactions, txRowIdMap }),
    [transactions, txRowIdMap],
  );

  return { dataSource };
}
